import { undo as OriginUndo } from 'prosemirror-history';
import { Fragment } from 'prosemirror-model';
import { NodeSelection, Selection, TextSelection } from 'prosemirror-state';
import { canSplit, liftTarget } from 'prosemirror-transform';
import { findCutBefore, getPrevNodeLastTextBlock, getStoreMarks } from '../../libs';
import { undoTextShortcut } from '../text-shortcut/shortcut-plugin';
var appendKeepMark = function (command) { return function (state, dispatch) {
    return command(state, function (tr) {
        var marks = state.storedMarks || (state.selection.$to.parentOffset && state.selection.$from.marks());
        if (marks)
            tr.ensureMarks(marks);
        dispatch && dispatch(tr);
    });
}; };
var doLiftNode = function (tr, pos) {
    var range = tr.doc.resolve(pos).blockRange();
    if (!range)
        return tr;
    if (range.depth) {
        var target = liftTarget(range);
        if (target === null || target === undefined)
            return tr;
        tr.lift(range, target ? target : range.depth - 1);
    }
    var $resPos = tr.doc.resolve(tr.mapping.map(range.start));
    if (!$resPos.depth || $resPos.parent.type.isTextblock) {
        if (!$resPos.nodeAfter)
            return tr;
        tr.setBlockType($resPos.pos, $resPos.pos + $resPos.nodeAfter.nodeSize, tr.doc.type.contentMatch.defaultType);
    }
    return tr;
};
var insertBreak = function (state, dispatch) {
    var selection = state.selection;
    if (selection.$cursor && selection.empty) {
        var marks = getStoreMarks(state);
        dispatch(state.tr.insert(selection.from, state.schema.nodes.break.create()).ensureMarks(marks));
        return true;
    }
    return false;
};
var deleteSelection = function (state, dispatch) {
    var _a = state.selection, empty = _a.empty, from = _a.from, $from = _a.$from;
    if (empty)
        return false;
    var tr = state.tr.deleteSelection();
    var textOffset = $from.parentOffset;
    var newSelection = Selection.near(tr.doc.resolve(from - 1 > 0 ? (textOffset ? from : from - 1) : 1));
    tr = tr.setSelection(newSelection).scrollIntoView();
    dispatch && dispatch(tr);
    return true;
};
/**
 * at the beginning of the content select the node with the `spec` `sylCard`
 * jump to the end of `textblock`
 * join any joinable
 */
var selectBefore = function (state, dispatch) {
    var $cursor = state.tr.selection.$cursor;
    if (!$cursor || $cursor.parentOffset || !$cursor.parent.content.size) {
        return false;
    }
    var $cut = findCutBefore($cursor);
    if (!$cut)
        return false;
    var before = $cut.nodeBefore;
    if (!before)
        return false;
    if (before.type.spec.sylCard) {
        var newSelection = NodeSelection.create(state.tr.doc, $cut.pos - before.nodeSize);
        dispatch && dispatch(state.tr.setSelection(newSelection));
        return true;
    }
    if (before.type.spec.role === 'list') {
        var _a = getPrevNodeLastTextBlock($cut), node = _a.node, pos = _a.pos;
        if (node && dispatch) {
            var tr = state.tr.deleteRange(pos + node.nodeSize - 1, $cursor.pos);
            dispatch(tr.setSelection(TextSelection.create(tr.doc, pos + node.nodeSize - 1)).scrollIntoView());
            return true;
        }
    }
    return false;
};
var splitBlock = function (state, dispatch) {
    var ref = state.selection;
    var $from = ref.$from, $to = ref.$to;
    if (state.selection instanceof NodeSelection && state.selection.node.isBlock) {
        if (!$from.parentOffset || !canSplit(state.doc, $from.pos)) {
            return false;
        }
        dispatch && dispatch(state.tr.split($from.pos).scrollIntoView());
        return true;
    }
    if (!$from.parent.isBlock) {
        return false;
    }
    // the paragraph is only recreated when there is no content, and the remaining types are created when at the end
    var needCreate = $from.parent.type === state.doc.type.contentMatch.defaultType
        ? !$from.parent.textContent
        : $to.parentOffset === $to.parent.content.size;
    var tr = state.tr;
    if (state.selection instanceof TextSelection) {
        tr.deleteSelection();
    }
    var defaultType = $from.depth === 0 ? undefined : $from.node(-1).contentMatchAt($from.indexAfter(-1)).defaultType;
    var types = needCreate && defaultType ? [{ type: defaultType }] : undefined;
    var can = canSplit(tr.doc, tr.mapping.map($from.pos), 1, types);
    if (!types && !can && canSplit(tr.doc, tr.mapping.map($from.pos), 1, defaultType && [{ type: defaultType }])) {
        types = [{ type: defaultType }];
        can = true;
    }
    if (can && defaultType) {
        tr.split(tr.mapping.map($from.pos), 1, types);
        if (!needCreate &&
            !$from.parentOffset &&
            $from.parent.type !== defaultType &&
            $from.node(-1).canReplace($from.index(-1), $from.indexAfter(-1), Fragment.from(defaultType.create()))) {
            tr.setNodeMarkup(tr.mapping.map($from.before()), defaultType);
        }
    }
    dispatch && dispatch(tr.scrollIntoView());
    return true;
};
var splitBlockKeepMarks = appendKeepMark(splitBlock);
var deleteZeroChar = function (state, dispatch) {
    var tr = state.tr;
    var $from = state.selection.$from;
    if (!$from.nodeBefore)
        return false;
    var zeroChars = [];
    var deleteLen = 0;
    var getMatchRes = function ($pos, node) {
        if (!node)
            return;
        var matchRes = node.textContent.match(/\u200B+$/);
        if (matchRes) {
            deleteLen = matchRes[0].length;
            zeroChars.push({ from: $pos.pos - matchRes[0].length, to: $pos.pos });
        }
    };
    getMatchRes($from, $from.nodeBefore);
    var $posBefore = tr.doc.resolve($from.pos - deleteLen - 1);
    getMatchRes($posBefore, $posBefore.nodeBefore);
    zeroChars.forEach(function (_a) {
        var from = _a.from, to = _a.to;
        return tr.delete(from, to);
    });
    dispatch && dispatch(tr);
    return false;
};
var clearAtHead = function (state, dispatch) {
    var _a = state.selection, $from = _a.$from, empty = _a.empty;
    if (!empty)
        return false;
    if ($from.pos !== 1 || $from.depth !== 1)
        return false;
    var tr = state.tr, doc = state.doc;
    var parentNode = $from.node();
    if (!parentNode.isTextblock || parentNode.childCount)
        return false;
    var afterPos = $from.after();
    var nodeAfter = state.doc.resolve(afterPos).nodeAfter;
    if (nodeAfter) {
        tr.delete($from.before(), afterPos);
        dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr);
        return true;
    }
    var defaultNodeType = doc.type.contentMatch.defaultType;
    var defaultNode = defaultNodeType.create();
    if (!parentNode.eq(defaultNode)) {
        dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setBlockType($from.before(), afterPos, defaultNodeType));
        return true;
    }
    return false;
};
var undo = function (state, dispatch) {
    return [undoTextShortcut, OriginUndo].some(function (fn) { return fn(state, dispatch); });
};
export { clearAtHead, deleteSelection, deleteZeroChar, doLiftNode, insertBreak, selectBefore, splitBlock, splitBlockKeepMarks, undo, };
//# sourceMappingURL=behavior.js.map